import { createAction, createAsyncAction, createReducer, isActionOf } from 'typesafe-actions'
import { combineReducers } from 'redux'
import { RefIntersection, RefKey } from '../../../responses/refs/refs'
import { createPaginatedAction, mergeKeyValueReducer } from '../../../utils/redux-utils'
import { combineEpics, Epic } from 'redux-observable'
import {
    catchError,
    filter,
    map,
    mapTo,
    mergeMap,
    scan,
    startWith,
    switchMap,
    takeUntil,
    withLatestFrom
} from 'rxjs/operators'
import { apiRequests } from '../../../http/config'
import { RootActions, RootState } from '../../root'
import { concat, EMPTY, forkJoin, from, merge, of } from 'rxjs'
import { Entity, UpdateData, } from '../../../types/UtilityTypes'
import { errorToast, toastFailedResponse } from '../../../components/controls/Toaster/Toaster'
import { ObjectCategory, ObjectType } from '../../../responses/objects'
import { Tag } from '../../../responses/tags'
import { omit } from '../../../utils/utils'
import * as Array from 'fp-ts/es6/Array'
import {
    diffEntityArray,
    diffEntityExecutorsArray, diffEntityNewExecutorsArray,
    isNew,
    prepareForServer,
    updatedEntities
} from '../../../utils/entity-utils'
import { innerListRequest } from '../../../utils/rx-operators'
import { ADTType, makeADT, ofType } from '@morphic-ts/adt'

const LIMIT = 50

type RefRecord<T> = Partial<Record<RefKey, T>>
type RefKeyValue<T> = { key: RefKey, value: T }

export const RefEditState = makeADT('type')({
    none: ofType<{type: 'none'}>(),
    update: ofType<{type: 'update', key: RefKey, ref: RefIntersection}>(),
    create: ofType<{type: 'create', key: RefKey}>(),
    delete: ofType<{type: 'delete', key: RefKey, ref: RefIntersection}>()
})

const actions = {
    startEdit: createAction('@REFS/START_EDIT')<{key: RefKey, ref: RefIntersection}>(),
    startCreate: createAction('@REFS/START_CREATE')<RefKey>(),
    startDelete: createAction('@REFS/START_DELETE')<{key: RefKey, ref: RefIntersection}>(),
    cancelEdit: createAction('@REFS/CANCEL_EDIT')(),
    refetch: createAction('@REFS/REFETCH')<RefKey>(),
    request: createPaginatedAction(
        '@REFS/REQUEST_INIT',
        '@REFS/REQUEST_MORE',
        '@REFS/REQUEST_SUCCESS',
        '@REFS/REQUEST_FAILURE',
    )<{ key: RefKey, search: string, category: ObjectCategory | null }, RefKeyValue<RefIntersection[]>, RefKey>(),
    add: createAsyncAction(
        '@REFS/ADD_REQUEST',
        '@REFS/ADD_SUCCESS',
        '@REFS/ADD_FAILURE',
    )<RefKeyValue<Partial<RefIntersection>>, RefKeyValue<RefIntersection>, void>(),
    update: createAsyncAction(
        '@REFS/UPDATE_REQUEST',
        '@REFS/UPDATE_SUCCESS',
        '@REFS/UPDATE_FAILURE',
    )<{ key: RefKey, old: RefIntersection, changes: Partial<RefIntersection> }, RefKeyValue<RefIntersection>, void>(),
    delete: createAsyncAction(
        '@REFS/DELETE_REQUEST',
        '@REFS/DELETE_SUCCESS',
        '@REFS/DELETE_FAILURE',
    )<undefined, RefKeyValue<number>, void>(),
}

export { actions as refsActions }

export interface RefsState {
    data: RefRecord<RefIntersection[]>,
    search: RefRecord<string>,
    category: ObjectCategory | null,
    editState: ADTType<typeof RefEditState>,
}

export const refsReducer = combineReducers<RefsState>({
    data: createReducer<RefRecord<RefIntersection[]>>({})
        .handleAction(actions.request.success, mergeKeyValueReducer),
    category: createReducer<ObjectCategory | null>(null)
        .handleAction(actions.request.init, (_, action) => action.payload.category),
    search: createReducer<RefRecord<string>>({})
        .handleAction(actions.request.init, (state, { payload }) => ({
            ...state,
            [payload.key]: payload.search,
        })),
    editState: createReducer<ADTType<typeof RefEditState>>(RefEditState.as.none({}))
        .handleAction(actions.startEdit, (_, { payload }) => RefEditState.as.update(payload))
        .handleAction(actions.startCreate, (_, { payload }) => RefEditState.as.create({key: payload}))
        .handleAction(actions.startDelete, (_, { payload }) => RefEditState.as.delete(payload))
        .handleAction([
            actions.cancelEdit,
            actions.update.success,
            actions.add.success,
            actions.delete.success,
        ], () => RefEditState.as.none({})),
})

const request: Epic<RootActions> = action$ => action$.pipe(
    filter(isActionOf(actions.request.init)),
    switchMap(({ payload }) => {
        const key: RefKey = payload.key
        const search: string = payload.search
        const category = payload.category?.id || undefined
        const all = ['client', 'department', 'work-type'].includes(key)

        // Преобразование событий в корректировки offset
        const plusLimit$ = action$.pipe(
            filter(action => isActionOf(actions.request.more)(action) && (action.payload === key)),
            mapTo(LIMIT),
        )
        const plusOne$ = action$.pipe(
            filter(action => isActionOf(actions.add.success)(action) && (action.payload.key === key)),
            mapTo(1),
        )
        const minusOne$ = action$.pipe(
            filter(action => isActionOf(actions.delete.success)(action) && (action.payload.key === key)),
            mapTo(-1),
        )

        // Хранение offset
        const offset$ = merge(plusLimit$, plusOne$, minusOne$).pipe(
            startWith(0),
            scan((acc, data) => ((acc + data) > 0 ? (acc + data) : 0), 0),
        )

        // Загрузка дополнительных данных
        const more$ = action$.pipe(
            filter(action => isActionOf(actions.request.more)(action) && (action.payload === key)),
            withLatestFrom(offset$),
            mergeMap(([, offset]) => apiRequests.dictionary.list({
                params: { key },
                query: { search, category, all, limit: LIMIT, offset },
            })),
            mergeMap(({ response }) => response.success ? of(response.rows) : EMPTY),
        )

        // Перезапрос или предварительная загрузка,
        return action$.pipe(
            startWith(actions.refetch(key)),
            filter((action) => isActionOf(actions.refetch)(action) && (action.payload === key)),
            withLatestFrom(offset$),
            mergeMap(([, offset]) => apiRequests.dictionary.list({
                params: { key },
                query: { search, category, all, limit: offset + LIMIT, offset: 0 },
            })),
            mergeMap(({ response }) => response.success ? of(response.rows) : EMPTY),
            // Объединение с дополнительными данными
            switchMap(data => more$.pipe(
                startWith([] as RefIntersection[]),
                scan((acc, result) => acc.concat(result), data),
            )),
            map(value => actions.request.success({ key, value })),
            takeUntil(action$.pipe(
                filter(action => isActionOf(actions.request.init)(action) && (action.payload.key === key)),
            )),
        )
    }),
)

const createTags = (tags: Tag[]) => innerListRequest('Не удалось добавить тег', tags.map(({ name }) => apiRequests.tag.add({ data: { name } })))
const createTypes = (types: ObjectType[]) => innerListRequest(
    'Не удалось добавить тип объекта',
    types.map(type => apiRequests.dictionary.add({
        params: { key: 'work-object-type' },
        data: omit('id')(type)
    }))
)
const updateTypes = (diffs: (Entity & UpdateData<ObjectType>)[]) => innerListRequest(
    'Не удалось обновить тип объекта',
    diffs.map(type => apiRequests.dictionary.update({
        params: { id: type.id, key: 'work-object-type' },
        data: omit('id')(type)
    }))
)

const add: Epic<RootActions> = action$ => action$.pipe(
    filter(isActionOf(actions.add.request)),
    mergeMap(({ payload }) => {
        const tags = payload.value.tags ?? []
        const types: ObjectType[] = payload.value.types ?? []
        return forkJoin({
            tags: createTags(tags),
            types: createTypes(types),
        }).pipe(
            mergeMap(({ tags, types }) => apiRequests.dictionary.add({
                data: prepareForServer({
                    ...payload.value,
                    executors: diffEntityNewExecutorsArray(payload.value.executors),
                    tags: diffEntityArray([], tags),
                    types: diffEntityArray([], types),
                }),
                params: { key: payload.key }
            })),
            map(({ response }) => response.success ?
                actions.add.success({ key: payload.key, value: response.row }) :
                actions.add.failure(toastFailedResponse(response, 'Добавление не удалось')),
            ),
            catchError(() => of(actions.add.failure(errorToast('Добавление не удалось')))),
        )
    }),
)

const update: Epic<RootActions> = action$ => action$.pipe(
    filter(isActionOf(actions.update.request)),
    mergeMap(action => {
        const { key, old, changes } = action.payload
        const { right: newTags, left: oldTags } = Array.partition(isNew)(changes.tags ?? [])
        const { right: newTypes, left: oldTypes } = Array.partition(isNew)(changes.types ?? [])

        return forkJoin({
            tags: createTags(newTags),
            types: createTypes(newTypes),
            updatedTypes: updateTypes(updatedEntities(old.types, oldTypes))
        }).pipe(
            mergeMap(({ tags, types }) => apiRequests.dictionary.update({
                data: prepareForServer({
                    ...changes,
                    departments: diffEntityArray(old.departments, changes.departments),
                    executors: diffEntityExecutorsArray(old.executors, changes.executors),
                    tags: diffEntityArray(old.tags, [...oldTags, ...tags]),
                    types: diffEntityArray(old.types, [...oldTypes ?? [], ...types]),
                }),
                params: { key, id: changes.id || 0 }
            })),
            mergeMap(({ response }) => response.success ?
                concat(
                    of(actions.update.success({ key, value: response.row })),
                    key === 'work-object-category' ? of(actions.refetch('work-object')): EMPTY,
                ) :
                of(actions.update.failure(toastFailedResponse(response, 'Обновление не удалось'))),
            ),
            catchError(() => of(actions.update.failure(errorToast('Обновление не удалось')))),
        )
    }),
)

const remove: Epic<RootActions, RootActions, RootState> = (action$, state$) => action$.pipe(
    filter(isActionOf(actions.delete.request)),
    mergeMap(() => RefEditState.match({
        delete: ({key, ref}) => apiRequests.dictionary.delete({
            params: { key, id: ref.id }
        }).pipe(
            map(({ response }) => response.success ?
                actions.delete.success({ key, value: response.row }) :
                actions.delete.failure(toastFailedResponse(response, 'Удаление не удалось'))
            )
        )
    }, () => EMPTY)(state$.value.pages.settings.refs.editState)),
)

const refetchOnChange: Epic<RootActions> = action$ => action$.pipe(
    filter(isActionOf([actions.add.success, actions.delete.success, actions.update.success])),
    mergeMap(({ payload }) => from([
        actions.refetch(payload.key),
    ])),
)

export const refsEpic = combineEpics(request, add, update, remove, refetchOnChange)